import React, { useContext } from "react";
import { ScreenTrigger } from "../../../lib/ScreenTrigger";
import { LoginContext } from "../../../lib/loginContext";
import { useNavigate, useLocation } from "react-router-dom";
import ProductCard from "../../Product/Components/ProductCard";
import { UPLOADS_URL, BASE_URL } from "../../../lib/apiConstants"; //

const ProductTab = ({ product }) => {
  const { isLoggedIn } = useContext(LoginContext);
  const navigate = useNavigate();
  const location = useLocation();
  const isLgOrLess = ScreenTrigger(992);

  const formattedArray = product?.specification
    ? Object.entries(product?.specification).map(([key, value]) => {
        const label = key
          .replace(/_/g, " ")
          .replace(/\b\w/g, (char) => char.toUpperCase());
        const formattedValue = value.replace(/\\n/g, "<br>");
        return { label, value: formattedValue };
      })
    : [];

  const isReaderProduct =
    product?.category?.toLowerCase() === "reader" || false;
  const flyer_link = product?.flyer_link || "#";
  const user_manual_link = product?.user_manual_link || "#";
  const ip_finder_link = product?.ip_finder_link || "#";
  const application_note_link = product?.application_note_link || "#";
  const wire_diagram_link = product?.wire_diagram_link || "#";
  const network_guide_link = product?.network_guide_link || "#";
  const quick_guide_link = product?.quick_guide_link || "#";

  const columnSplit1 = isLgOrLess
    ? Math.ceil(formattedArray.length / 2)
    : Math.ceil(formattedArray.length / 3);
  const columnSplit2 = isLgOrLess ? columnSplit1 * 2 : columnSplit1 * 2;
  const isCredentialProduct =
    product?.category?.toLowerCase() === "credential" || false;

  // Enhanced helper function for downloading files with better security
  const downloadFile = async (filename, fileUrl) => {
    try {
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = filename;
      link.rel = "noopener noreferrer";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      console.log(`Downloading ${filename}`);
    } catch (error) {
      console.error(`Error downloading ${filename}:`, error);
    }
  };

  // Fixed function for IP Finder zip download
  const downloadIPFinderZip = async () => {
    const zipFilename = "IP_Installer.zip";
    const zipUrl = `${UPLOADS_URL}IP_Installer.zip`;

    try {
      await downloadFile(zipFilename, zipUrl);
    } catch (error) {
      console.error("Error downloading ZIP file:", error);
      console.warn("ZIP file download failed, trying EXE instead");
      try {
        const exeUrl = `${UPLOADS_URL}${ip_finder_link}`;
        await downloadFile("IP_Installer.exe", exeUrl);
      } catch (exeError) {
        console.error("EXE download also failed:", exeError);
        alert("Download failed. Please try again later or contact support.");
      }
    }
  };

  const handleLoginClick = () => {
    navigate("/login", { state: { from: location.pathname } });
  };

  const slugify = (text) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const suggestedProductsData = (product?.suggested_products ?? [])
    .map((sug) => {
      const full = (product?.referenced_products ?? []).find(
        (ref) => ref.title === sug.label
      );
      if (!full) return null;
      return {
        id: full.id || sug.value,
        title: full.title,
        info: full.short_desc,
        offerPrice: full.price?.level_2_price,
        actualPrice: full.price?.level_1_price,
        image: `${BASE_URL}${full.image?.url}`, // ← Now BASE_URL is defined
        slug: full.slug || slugify(full.title),
        readers_icons: full.readers_icons,
      };
    })
    .filter(Boolean);

  return (
    <div className="mt-6 max-[450px]:mt-4">
      {formattedArray.length > 0 && (
        <>
          <h2 className="text-[#121212] font-[Poppins] text-[22px] font-bold leading-[150%] text-left mb-6">
            Product Specifications
          </h2>

          <div className="border-t border-b pb-3 border-black">
            <div className="mt-4">
              <div className="grid grid-cols-3 max-[992px]:grid-cols-2 max-sm:grid-cols-1 gap-y-6 relative">
                <div className="space-y-6 sm:pr-[30px]">
                  {formattedArray.slice(0, columnSplit1).map((item, index) => (
                    <div key={index} className="flex gap-x-2 items-start">
                      <div className="text-[#000] font-[Poppins] text-[13px] font-medium min-w-[158px] max-w-[150px] max-sm:max-w-[130px] max-sm:min-w-[130px] leading-6">
                        {item?.label}
                      </div>
                      <span
                        className="text-[#000] font-[Poppins] text-[13px] font-normal leading-6"
                        dangerouslySetInnerHTML={{ __html: item?.value }}
                      />
                    </div>
                  ))}
                </div>
                <div className="space-y-6 sm:border-l border-gray-400 sm:px-[30px]">
                  {formattedArray
                    .slice(columnSplit1, columnSplit2)
                    .map((item, index) => (
                      <div key={index} className="flex gap-x-2 items-start">
                        <div className="text-[#000] text-[13px] font-medium min-w-[158px] max-w-[150px] max-sm:max-w-[130px] max-sm:min-w-[130px] leading-6">
                          {item?.label}
                        </div>
                        <span
                          className="text-[#000] text-[13px] font-normal leading-6"
                          dangerouslySetInnerHTML={{ __html: item?.value }}
                        />
                      </div>
                    ))}
                </div>
                <div className="space-y-6 sm:border-l sm:border-gray-400 sm:pl-[30px] max-[992px]:border-none max-[992px]:pl-0">
                  {formattedArray.slice(columnSplit2).map((item, index) => (
                    <div key={index} className="flex gap-x-2 items-start">
                      <div className="text-[#000] text-[13px] font-medium min-w-[158px] max-w-[150px] max-sm:max-w-[130px] max-sm:min-w-[130px] leading-6">
                        {item?.label}
                      </div>
                      <span
                        className="text-[#000] text-[13px] font-normal leading-6"
                        dangerouslySetInnerHTML={{ __html: item?.value }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {suggestedProductsData.length > 0 && (
        <>
          <h2 className="text-[#121212] font-[Poppins] text-[22px] font-bold leading-[150%] text-left mb-4 mt-7">
            Suggested Products
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-y-[24px] gap-x-[15px] mt-4 lg:mt-8 pr-3 md:pr-0">
            {suggestedProductsData.map((item) => (
              <ProductCard
                key={item?.id}
                title={item.title}
                info={item.info}
                offer={item.offerPrice}
                actual={item.actualPrice}
                img={item.image}
                id={item.id}
                slug={item?.slug}
                readers_icons={item?.readers_icons}
              />
            ))}
          </div>
        </>
      )}

      {!isLoggedIn && (
        <div className="mt-10 text-center">
          <p className="text-[16px] text-gray-700">
            *Please{" "}
            <span
              onClick={handleLoginClick}
              className="text-blue-600 cursor-pointer hover:underline not-italic font-semibold"
            >
              Login
            </span>{" "}
            to see product manuals*
          </p>
        </div>
      )}
    </div>
  );
};

export default ProductTab;
